// ─── ISO Week Helpers ─────────────────────────────────────────────────────────

/**
 * Calculates the ISO week number from a YYYY-MM-DD date string.
 * ISO weeks start on Monday. Week 1 is the week containing the first Thursday.
 */
function getISOWeekNumber(dateString) {
  const date = new Date(dateString + 'T00:00:00');
  const thursday = new Date(date);
  thursday.setDate(date.getDate() + (4 - (date.getDay() || 7)));
  const yearStart = new Date(thursday.getFullYear(), 0, 1);
  return Math.ceil(((thursday - yearStart) / 86400000 + 1) / 7);
}

/**
 * Returns the ISO week year for a YYYY-MM-DD date string.
 * This may differ from the calendar year at year boundaries.
 * Example: 2024-12-30 belongs to ISO week year 2025.
 */
function getISOWeekYear(dateString) {
  const date = new Date(dateString + 'T00:00:00');
  const thursday = new Date(date);
  thursday.setDate(date.getDate() + (4 - (date.getDay() || 7)));
  return thursday.getFullYear();
}

// ─── Duration Helper ──────────────────────────────────────────────────────────

/**
 * Formats a totalMinutes value into an HH:mm string.
 * Matches the pattern used by ScheduleCalculationService.
 * Example: 450 -> "07:30"
 */
function formatDuration(totalMinutes) {
  if (totalMinutes == null || isNaN(totalMinutes)) return '00:00';
  const absMinutes = Math.abs(totalMinutes);
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

// ─── ScheduleReportModel ──────────────────────────────────────────────────────

/**
 * Report model for a single shift entry used across all schedule report types.
 *
 * Data source: companies/{companyId}/schedules/{staffId}/shifts/{shiftId}
 * Created via: ScheduleReportModel.fromShiftModel(shiftModel, staffId, staffName)
 *
 * staffId is passed separately because it is the parent document ID in Firestore,
 * not stored on the shift document itself.
 * staffName is resolved from the staffMembers array in ReportService.
 *
 * weekYear is stored alongside weekNumber to handle cross-year ISO week boundaries
 * correctly when grouping shifts for the Weekly Summary report type.
 */
export class ScheduleReportModel {
  constructor({
    staffId,
    staffName,
    date,
    startTime,
    endTime,
    totalMinutes,
    departmentId,
    weekNumber,
    weekYear,
  }) {
    this.staffId = staffId || '';
    this.staffName = staffName || '';
    this.date = date || '';
    this.startTime = startTime || '';
    this.endTime = endTime || '';
    this.totalMinutes = typeof totalMinutes === 'number' ? totalMinutes : 0;
    this.departmentId = departmentId || '';
    this.weekNumber = typeof weekNumber === 'number' ? weekNumber : 0;
    this.weekYear = typeof weekYear === 'number' ? weekYear : 0;
  }

  // ─── Instance Methods ────────────────────────────────────────────────────────

  /**
   * Returns a unique composite key for ISO week grouping.
   * Used in the Weekly Summary report type to group shifts across year boundaries.
   * Format: "YYYY-WXX"
   * Example: "2025-W03"
   */
  getWeekKey() {
    return `${this.weekYear}-W${String(this.weekNumber).padStart(2, '0')}`;
  }

  /**
   * Returns the formatted duration string derived from totalMinutes.
   * Format: HH:mm
   * Example: 450 minutes -> "07:30"
   */
  getFormattedDuration() {
    return formatDuration(this.totalMinutes);
  }

  /**
   * Returns the date formatted as DD.MM.YYYY from the stored YYYY-MM-DD string.
   * Used in table display across preview components.
   */
  getFormattedDate() {
    if (!this.date) return '';
    const [year, month, day] = this.date.split('-');
    if (!year || !month || !day) return this.date;
    return `${day}.${month}.${year}`;
  }

  // ─── Static Helpers ──────────────────────────────────────────────────────────

  /**
   * Exposed for use in ReportService and preview components
   * when computing week numbers outside of an instance context.
   */
  static getISOWeekNumber(dateString) {
    return getISOWeekNumber(dateString);
  }

  /**
   * Exposed for use in ReportService and preview components
   * when computing week years outside of an instance context.
   */
  static getISOWeekYear(dateString) {
    return getISOWeekYear(dateString);
  }

  /**
   * Exposed for use in ReportService CSV generation and preview components
   * when formatting durations outside of an instance context.
   */
  static formatDuration(totalMinutes) {
    return formatDuration(totalMinutes);
  }

  // ─── Factory Method ──────────────────────────────────────────────────────────

  /**
   * Creates a ScheduleReportModel from a ShiftModel instance.
   *
   * @param {object} shiftModel - ShiftModel instance from ShiftModel.fromFirestore(doc)
   * @param {string} staffId    - The staffId from the parent Firestore document path
   * @param {string} staffName  - Resolved from the staffMembers array in ReportService
   * @returns {ScheduleReportModel}
   */
  static fromShiftModel(shiftModel, staffId, staffName) {
    const date = shiftModel.date || '';

    return new ScheduleReportModel({
      staffId: staffId || '',
      staffName: staffName || '',
      date,
      startTime: shiftModel.startTime || '',
      endTime: shiftModel.endTime || '',
      totalMinutes: shiftModel.totalMinutes || 0,
      departmentId: shiftModel.departmentId || '',
      weekNumber: date ? getISOWeekNumber(date) : 0,
      weekYear: date ? getISOWeekYear(date) : 0,
    });
  }
}